import { FC, useEffect, useState } from "react";
import {
  LdButton,
  LdIcon,
  LdInput,
  LdLabel,
  LdOption,
  LdSelect,
} from "@emdgroup-liquid/liquid/dist/react";
import moment, { isMoment } from "moment";
import { nanoid } from "nanoid";
import QuestionForm from "./QuestionForm";

import {
  assertQuestion,
  FormValidationError,
  Question,
  SurveyRequest,
} from "typings/app/types";
import { SurveyFrequency } from "typings/app/types";

interface Props {
  children?: string | JSX.Element | JSX.Element[];
  isLoading?: boolean;
  onSubmit: (arg0: SurveyRequest) => void;
  onCancel?: () => void;
}

const SurveyForm: FC<Props> = ({
  isLoading = false,
  onSubmit,
  onCancel,
  children,
}) => {
  const [surveyName, setSurveyName] = useState<string | undefined>();
  const [startDate, setStartDate] = useState<string | undefined>(
    moment().format("YYYY-MM-DD")
  );
  const [frequency, setFrequency] = useState<SurveyFrequency>(
    SurveyFrequency.once
  );
  const [endDate, setEndDate] = useState<string | undefined>();
  const [questions, setQuestions] = useState<{ [key: string]: unknown }>({});
  const [formError, setFormError] = useState<FormValidationError>({});

  const addQuestion = (key: string, question: {}) => {
    setQuestions((state) => ({ ...state, [key]: question }));
  };

  const removeQuestion = (key: string) => {
    setQuestions((state) => {
      const newState = { ...state };
      delete newState[key];
      return newState;
    });
  };

  const addError = (key: string, message: string) => {
    setFormError((state) => ({ ...state, [key]: message }));
  };

  const resolveError = (key: string) => {
    setFormError((state) => {
      const newState = { ...state };
      delete newState[key];
      return newState;
    });
  };

  const validateQuestions = () => {
    const questionsArray: Question[] = [];
    Object.keys(questions).forEach((key) => {
      if (assertQuestion(questions[key])) {
        questionsArray.push(questions[key] as Question);
      } else {
        return addError("question", `${key} is not a valid question object`);
      }
    });
    resolveError("questions");
    return questionsArray;
  };

  const validate = () => {
    if (surveyName === undefined) {
      addError("surveyName", "Name must not be empty");
    } else {
      resolveError("surveyName");
    }
    if (startDate === undefined) {
      addError("startDate", "Start date must not be empty");
    } else if (!isMoment(moment(startDate))) {
      addError("startDate", "Start date must be a valid date");
    } else {
      resolveError("startDate");
    }
    if (questions.length === 0) {
      addError("questions", "You must add at least one question");
    } else {
      resolveError("questions");
    }
    if (endDate === undefined && frequency === SurveyFrequency.once) {
      addError("endDate", "Please set and end date");
    } else {
      resolveError("endDate");
    }
  };

  useEffect(validate, [surveyName, startDate, frequency, endDate, questions]);

  const isValid = () => Object.keys(formError).length === 0;

  const handleAddQuestion = () => {
    addQuestion(nanoid(), {});
  };

  const handleSubmit = () => {
    let questionsArray = validateQuestions();
    if (isValid()) {
      const result = {
        surveyName,
        startDate,
        endDate,
        frequency,
        questions: questionsArray,
      } as SurveyRequest;
      onSubmit(result);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  return (
    <div className="w-full md:w-max">
      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-ld-24 mb-ld-32">
        <LdLabel>
          Name
          <LdInput
            placeholder="New Survey"
            tone="dark"
            value={surveyName}
            onInput={(ev) => {
              setSurveyName((ev.target as HTMLLdInputElement).value);
            }}
            onBlur={(ev) => {
              setSurveyName(ev.target.value);
            }}
          />
        </LdLabel>
        <LdLabel>
          <span className="flex justify-between">Frequency</span>
          <LdSelect
            disabled={isLoading}
            onInput={
              ((
                event: React.SyntheticEvent<
                  HTMLInputElement,
                  CustomEvent<SurveyFrequency[]>
                >
              ) => {
                setFrequency(event.nativeEvent.detail[0]);
              }) as unknown as any
            }
            placeholder="Select"
            preventDeselection
          >
            {Object.keys(SurveyFrequency).map((key) => {
              const val = SurveyFrequency[key as keyof typeof SurveyFrequency];
              return (
                <LdOption key={key} value={val} selected={frequency === val}>
                  {val}
                </LdOption>
              );
            })}
          </LdSelect>
        </LdLabel>
        <LdLabel>
          Start Date
          <LdInput
            disabled={isLoading || undefined}
            placeholder="Start Date"
            tone="dark"
            type="date"
            value={startDate}
            onInput={(ev) => {
              setStartDate((ev.target as HTMLLdInputElement).value);
            }}
            onBlur={(ev) => {
              setStartDate((ev.target as HTMLLdInputElement).value);
            }}
          />
        </LdLabel>
        <LdLabel>
          End Date
          <LdInput
            disabled={isLoading || undefined}
            placeholder="End Date"
            tone="dark"
            type="date"
            value={endDate}
            onInput={(ev) => {
              setEndDate((ev.target as HTMLLdInputElement).value);
            }}
            onBlur={(ev) => {
              setEndDate((ev.target as HTMLLdInputElement).value);
            }}
          />
        </LdLabel>
      </div>
      <div className="w-full flex flex-col gap-ld-24 mb-ld-32 divide-y-2">
        {Object.keys(questions).map((key) => (
          <div className="flex flex-row">
            <QuestionForm
              disabled={isLoading || undefined}
              key={key}
              onChange={(question) => addQuestion(key, question)}
            />
            <LdLabel className="ml-ld-8 mt-ld-16">
              <span className="invisible">-</span>
              <LdButton
                disabled={isLoading}
                mode="danger"
                type="button"
                onClick={() => removeQuestion(key)}
              >
                <LdIcon name="cross" aria-label="delete" />
              </LdButton>
            </LdLabel>
          </div>
        ))}
      </div>
      <div className="w-full flex items-stretch mb-ld-32">
        <LdButton
          disabled={isLoading}
          style={{ width: "100%" }}
          mode="secondary"
          type="button"
          onClick={handleAddQuestion}
        >
          <LdIcon name="add" />
          Add question
        </LdButton>
      </div>
      <div className="w-full grid md:flex md:justify-between pb-ld-32">
        <div className="l-0">{children}</div>
        <div className="flex flex-grow-0" />
        <div className="grid grid-cols-2 gap-ld-16">
          <LdButton
            disabled={isLoading}
            onClick={handleCancel}
            mode="secondary"
            type="button"
          >
            Cancel
          </LdButton>
          <LdButton
            disabled={!isValid() || isLoading}
            progress={isLoading ? "pending" : undefined}
            type="button"
            onClick={handleSubmit}
          >
            Submit
          </LdButton>
        </div>
      </div>
    </div>
  );
};

export default SurveyForm;
